// n8n-chat/src/components/ui/auth-layout.tsx
import type { ReactNode } from "react";
import Image from "next/image";

export type Testimonial = {
  avatarSrc: string;
  name: string;
  handle: string;
  text: ReactNode;
};

export function AuthLayout({
  children,
  heroImageSrc,
  testimonials = [],
}: {
  readonly children: ReactNode;
  readonly heroImageSrc?: string;
  readonly testimonials?: Testimonial[];
}) {
  return (
    <div className="flex min-h-[calc(100vh-80px)] w-full flex-col md:flex-row">
      <section className="flex flex-1 items-center justify-center bg-black p-4 lg:p-8">
        <div className="w-full max-w-sm">{children}</div>
      </section>

      {heroImageSrc ? (
        <section className="relative hidden flex-1 bg-black p-4 lg:block">
          <div className="absolute inset-4 overflow-hidden rounded-3xl border border-white/10 shadow-2xl">
            <Image
              src={heroImageSrc}
              alt=""
              fill
              priority
              sizes="(min-width: 1024px) 50vw, 0px"
              className="object-cover"
            />
            <div className="absolute inset-0 bg-linear-to-t from-black/80 via-black/20 to-transparent" />
          </div>

          {testimonials[0] ? (
            <div className="absolute bottom-8 left-1/2 z-10 flex w-full -translate-x-1/2 justify-center px-12">
              <TestimonialCard testimonial={testimonials[0]} />
            </div>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}

export function GlassInputWrapper({
  children,
}: {
  readonly children: ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm transition-colors focus-within:border-violet-400/70 focus-within:bg-violet-500/10">
      {children}
    </div>
  );
}

function TestimonialCard({
  testimonial,
}: {
  readonly testimonial: Testimonial;
}) {
  return (
    <div className="flex w-64 items-start gap-3 rounded-3xl border border-white/10 bg-black/40 p-5 backdrop-blur-xl">
      <div className="relative min-h-10 h-10 min-w-10 w-10 overflow-hidden rounded-2xl">
        <Image
          src={testimonial.avatarSrc}
          alt="avatar"
          fill
          sizes="40px"
          className="object-cover"
        />
      </div>
      <div className="text-sm leading-snug">
        <p className="font-display font-medium text-white">
          {testimonial.name}
        </p>
        <p className="font-sans text-white/50">{testimonial.handle}</p>
        <p className="mt-1 font-sans text-white/80">{testimonial.text}</p>
      </div>
    </div>
  );
}

export function GoogleIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-5 w-5"
      viewBox="0 0 48 48"
    >
      <path
        fill="#FFC107"
        d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-2.641-.21-5.236-.611-7.743z"
      />
      <path
        fill="#FF3D00"
        d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571l6.19 5.238C42.022 35.026 44 30.038 44 24c0-2.641-.21-5.236-.611-7.743z"
      />
    </svg>
  );
}
